import { Button, Card, Divider, InfoTip, Metric, Text } from "@/design-system";
import { MessageCircle } from "lucide-react";
import { CET_EXPLANATION } from "../constants";
import { formatCurrency } from "../utils/finance";

export type Simulation = {
  installments: number;
  payment: number;
  downPayment: number;
  totalPaid: number;
  totalInterest: number;
  monthlyRate: string;
  yearlyRate: string;
  monthlyCet: string;
  yearlyCet: string;
};

type SimulationSummaryProps = {
  simulation: Simulation;
  onShare: () => void;
};

/**
 * Resultado do simulador. Só a parcela fica em `aria-live`: quem usa leitor de
 * tela ouve o número novo a cada campo alterado, sem a ficha inteira repetida.
 */
export const SimulationSummary = ({
  simulation,
  onShare,
}: SimulationSummaryProps) => (
  <Card className="flex flex-col gap-6">
    <div aria-live="polite">
      <Metric
        label="Parcela do cliente"
        value={`${simulation.installments}x de ${formatCurrency(simulation.payment)}`}
        hint={
          simulation.downPayment > 0
            ? `Mais entrada de ${formatCurrency(simulation.downPayment)}`
            : "Sem entrada"
        }
      />
    </div>
    <Divider />
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="flex flex-col gap-1">
        <Text as="dt" size="small" tone="subtle">
          Total a prazo
        </Text>
        <Text as="dd" weight="semibold" numeric>
          {formatCurrency(simulation.totalPaid)}
        </Text>
      </div>
      <div className="flex flex-col gap-1">
        <Text as="dt" size="small" tone="subtle">
          Juros e tarifas
        </Text>
        <Text as="dd" weight="semibold" numeric>
          {formatCurrency(simulation.totalInterest)}
        </Text>
      </div>
      <div className="flex flex-col gap-1">
        <Text as="dt" size="small" tone="subtle">
          Taxa de juros
        </Text>
        <Text as="dd" weight="semibold" numeric>
          {simulation.monthlyRate} a.m. · {simulation.yearlyRate} a.a.
        </Text>
      </div>
      <div className="flex flex-col gap-1">
        <Text
          as="dt"
          size="small"
          tone="subtle"
          className="inline-flex items-center gap-1"
        >
          Custo efetivo total (CET)
          <InfoTip label="O que é CET?">{CET_EXPLANATION}</InfoTip>
        </Text>
        <Text as="dd" weight="semibold" numeric>
          {simulation.monthlyCet} a.m. · {simulation.yearlyCet} a.a.
        </Text>
      </div>
    </dl>
    <Button
      variant="secondary"
      navigates="external"
      iconLeft={<MessageCircle aria-hidden="true" className="size-4" />}
      onClick={onShare}
      className="w-full sm:w-auto sm:self-start"
    >
      Enviar simulação no WhatsApp
    </Button>
  </Card>
);
